import { StatusIndicator, type StatusShape, type StatusTone } from '../StatusIndicator/StatusIndicator';

/**
 * A buying standard's price state — design-system §5's provisional *Price state* row (Rev 33), shared
 * by the Pricing page's Standards book and the account page's standards panel (F26-b FR-U2;
 * F26-b WI-1.3 Decision 11).
 *
 * `StatusIndicator` deliberately encodes no state vocabulary, so the vocabulary lives here, in one
 * component both screens render. **The labels are provisional**: the PRD names no price states, so
 * they are this project's wording until Empire confirms them (§5).
 *
 * | State | Tone | Dot | Label |
 * |---|---|---|---|
 * | A published price covers today | `tone/positive` | solid | Current |
 * | A private-label standard with no published price (normal under OQ-61's default) | `tone/neutral` | half-filled | Priced per quote |
 * | Otherwise | `tone/notice` | ring | No current price |
 *
 * The long form is the accessible name. The price itself is never inside the indicator; it sits in
 * its own *Price / kg* column (§9 *Money*).
 */
export const PRICE_STATES = ['current', 'priced-per-quote', 'no-current-price'] as const;
export type PriceState = (typeof PRICE_STATES)[number];

interface PriceStatePresentation {
    readonly tone: StatusTone;
    readonly shape: StatusShape;
    readonly label: string;
    readonly description: string;
}

export const PRICE_STATE_PRESENTATION: Readonly<Record<PriceState, PriceStatePresentation>> = {
    current: {
        tone: 'positive',
        shape: 'solid',
        label: 'Current',
        description: 'Current — a published price covers today',
    },
    'priced-per-quote': {
        tone: 'neutral',
        shape: 'half',
        label: 'Priced per quote',
        description: 'Priced per quote — a private-label standard is priced on each deal',
    },
    'no-current-price': {
        tone: 'notice',
        shape: 'ring',
        label: 'No current price',
        description: 'No current price — no published price covers today',
    },
};

/**
 * The state, derived on the client — no server field carries it (contract client-side fact 5): a
 * price → *Current*; no price on a private-label standard → *Priced per quote*; otherwise *No current
 * price*. It takes primitives rather than a wire type, so `src/ui` imports nothing from `src/lib`.
 */
export function priceStateOf(hasPublishedPrice: boolean, standardType: 'bulk' | 'private_label'): PriceState {
    if (hasPublishedPrice) {
        return 'current';
    }
    return standardType === 'private_label' ? 'priced-per-quote' : 'no-current-price';
}

export interface PriceStateIndicatorProps {
    readonly state: PriceState;
    readonly 'data-testid'?: string;
}

export function PriceStateIndicator({ state, 'data-testid': testId }: PriceStateIndicatorProps) {
    const { tone, shape, label, description } = PRICE_STATE_PRESENTATION[state];
    return (
        <StatusIndicator
            description={description}
            label={label}
            shape={shape}
            tone={tone}
            {...(testId === undefined ? {} : { 'data-testid': testId })}
        />
    );
}
